import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { first, map, switchMap, shareReplay } from 'rxjs/operators';
import { AccountService } from './account.service';

/**
 * The tokens an author can insert into CMS text (#208).
 *
 * picaluna-v3 resolves `{{copy:…}}` and `{{cost:…}}` at build time; this is
 * the list the editor offers so nobody has to remember — or type — one. Cost
 * tokens in particular address a line item by its opaque EAV id
 * (`id-itm-7f3a`), which is exactly the sort of thing a picker exists for.
 *
 * `preview` is the value as it stands RIGHT NOW. It is there so an author
 * choosing between "Transfer" and "Mortuary Care" sees $395.00 and $425.00
 * beside them, rather than two labels and a guess. It is a convenience, not
 * a contract: the site resolves from its own snapshot at build time, so a
 * token inserted here and published tomorrow renders tomorrow's value.
 */
export interface TokenChoice {
  /** What gets inserted, e.g. `{{cost:newcastle-burial.id-itm-7f3a}}`. */
  token: string;
  /** Human label for the list, e.g. "Newcastle Burial → Transfer". */
  label: string;
  /** Current resolved value, shown beside the label. */
  preview: string;
  /** Grouping header in the picker. */
  group: string;
}

const aud = (n: number) =>
  n % 1 === 0
    ? `$${n.toLocaleString('en-AU')}`
    : `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

@Injectable({ providedIn: 'root' })
export class TokenCatalogueService {
  private cached?: Observable<TokenChoice[]>;

  constructor(private accountService: AccountService) {}

  /**
   * Every insertable token, grouped. Cached for the session — the catalogue
   * changes only when funeral costs or template copy are edited, and a
   * re-fetch per keystroke would make the autocomplete feel heavy.
   *
   * ensureFullAccount() first, for the same reason the sections editor does
   * it: account$ replays a content-STRIPPED account, so an unguarded read
   * yields an empty catalogue that looks identical to "no tokens exist".
   */
  list(): Observable<TokenChoice[]> {
    if (!this.cached) {
      this.cached = this.accountService.ensureFullAccount().pipe(
        switchMap(() => this.accountService.account$),
        first(),
        map((a: any) => this.build(a)),
        shareReplay(1)
      );
    }
    return this.cached;
  }

  /** Drop the cache after a content save so new records show up. */
  invalidate(): void {
    this.cached = undefined;
  }

  private build(a: any): TokenChoice[] {
    const out: TokenChoice[] = [];
    const content = a?.data?.content ?? {};

    // ── Template copy: site-wide fragments (phone number, help-band copy) ──
    const rawCopy = content.templateCopy;
    for (const frag of this.items(rawCopy)) {
      if (typeof frag.key !== 'string' || !frag.key) continue;
      out.push({
        token: `{{copy:${frag.key}}}`,
        label: frag.name || frag.key,
        preview: this.truncate(frag.copy ?? ''),
        group: 'Template copy',
      });
    }

    // ── Funeral cost line items, per record ───────────────────────────────
    // Addressed as <record-slug>.<item-id>; the slug is what picaluna-v3's
    // funeralCosts collection keys on, NOT the Conductor record id.
    for (const fc of this.items(a?.data?.funeralCosts)) {
      const slug = typeof fc.slug === 'string' && fc.slug ? fc.slug : null;
      if (!slug || fc._meta?.deleted) continue;
      /* The REGION leads the label. Every region carries its own copy of the
         same three records ("Burial with a Funeral Service" exists five times
         over), so a label built from name + item alone produced eight
         identical rows differing only in price — the author could not tell
         which one was Newcastle's. Region first also groups the regions
         together once the list is sorted. */
      const recordName = fc.name || slug;
      const region = typeof fc.region === 'string' && fc.region ? fc.region : null;
      const regionLabel = region && region !== 'global' ? this.titleCase(region) : 'All regions';
      for (const [itemId, item] of this.entries(fc.items)) {
        if (!item || typeof item !== 'object' || item._meta?.deleted) continue;
        if (typeof item.label !== 'string' || !item.label.trim()) continue;
        out.push({
          token: `{{cost:${slug}.${itemId}}}`,
          label: `${regionLabel} · ${recordName} → ${item.label}`,
          // An item with no price is still listed: the author needs to see
          // that it exists and is blank, rather than wonder why it is absent.
          preview: typeof item.price === 'number' ? aud(item.price) : '(no price set)',
          group: 'Funeral costs',
        });
      }
    }

    return out.sort((x, y) => x.group.localeCompare(y.group) || x.label.localeCompare(y.label));
  }

  /**
   * Id/value pairs of an EAV collection, or NOTHING when the ids are not real.
   *
   * The bug this exists to prevent: a collection round-trips as an id-keyed
   * object OR as an array, and `Object.entries` on an array yields the array
   * INDICES as keys. The picker therefore offered `{{cost:<slug>.3}}` —
   * positional, so it broke the build immediately and, had it resolved, would
   * have silently changed meaning the moment anyone reordered the items.
   *
   * An array is read through each item's OWN `id`/`_id`; an item without one
   * is skipped entirely rather than falling back to its position. A token
   * that cannot be addressed stably is better absent than wrong.
   */
  private entries(raw: any): Array<[string, any]> {
    if (Array.isArray(raw)) {
      return raw
        .map((item): [string, any] | null => {
          const id = item?.id ?? item?._id;
          return typeof id === 'string' && id ? [id, item] : null;
        })
        .filter((e): e is [string, any] => e !== null);
    }
    if (raw && typeof raw === 'object') return Object.entries<any>(raw);
    return [];
  }

  /** EAV collections round-trip as id-keyed objects OR arrays; accept both. */
  private items(raw: any): any[] {
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (raw && typeof raw === 'object') return Object.values(raw).filter(Boolean) as any[];
    return [];
  }

  /** `central-coast` → `Central Coast`. */
  private titleCase(s: string): string {
    return s
      .split(/[-_]/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  private truncate(s: string, max = 60): string {
    const one = s.replace(/\s+/g, ' ').trim();
    return one.length > max ? `${one.slice(0, max - 1)}…` : one;
  }
}
